import { ArrowLeft, ArrowRight, Check, ClipboardCheck, ClipboardList, Copy, Info, Users, UserCheck, X } from "lucide-react"
import type { ReactNode } from "react"
import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils/helpers"
import { getRoleDisplayName } from "@/constants/roles"
import { formatPhoneDisplay } from "@/features/property/utils/formatPhone"
import { formatDate } from "@/lib/utils/formatters"
import { SectionCard } from "@/components/ui/SectionCard"
import companyFormStyles from "@/features/company/styles/companyAdd.module.css"
import tabStyles from "@/features/company/styles/companyInfoTab.module.css"
import { transferContactsHelpersHidden } from "../../utils/transferProcessFragment"
import { usesTransferWizardCardLayout } from "../../utils/transferPropertyCompanyWizard"
import { StaffUserEditLink } from "../StaffUserEditLink"
import type { TransferContactsTabProps } from "../../types"

const employeeTableWrapClass = "min-w-0 overflow-x-auto rounded-lg border border-slate-200"
const employeeTransferTableScrollClass =
  "h-[min(28rem,75vh)] overflow-x-auto overflow-y-auto rounded-lg border border-slate-200"

function TransferEmployeeSection({
  useCardLayout,
  icon,
  title,
  testId,
  titleTestId,
  headerActions,
  children,
}: {
  useCardLayout: boolean
  icon: LucideIcon
  title: string
  testId?: string
  titleTestId?: string
  headerActions?: ReactNode
  children: ReactNode
}) {
  if (!useCardLayout) return <>{children}</>
  return (
    <SectionCard
      icon={icon}
      title={title}
      testId={testId}
      titleTestId={titleTestId}
      headerActions={headerActions}
    >
      {children}
    </SectionCard>
  )
}

export function TransferContactsTab({ ctx }: TransferContactsTabProps) {
  const {
    selectedTab,
    showEmployeeTab,
    hasPolicy,
    fragment,
    selectedSubTab,
    setSelectedSubTab,
    staffList,
    removestaffList,
    transferstaffList,
    addedstaffList,
    selectedRemoveRoleList,
    selectedTransferRoleList,
    selectedAddRoleList,
    listedmanagementList,
    commitDone,
    verifyDone,
    setUserEditModalUserId,
    formState,
    processing,
    openAddEmployeeModal,
    draftTransferStaff,
    selectAllTransfer,
    userSelectedTransfer,
    completeStaffTransfer,
    completeSplitPropertyTransfer,
    completeVerify,
  } = ctx
  if (selectedTab !== "contactsinfo" || !showEmployeeTab || !fragment) return null

  const isTransferCompany = fragment === "transfer_company"
  const isSplitProperty = fragment === "split_property"
  const useCardLayout = usesTransferWizardCardLayout(fragment)
  const panelClassName = cn(
    useCardLayout ? tabStyles.tabContent : "space-y-6",
    "animate-in fade-in duration-300",
  )
  const subPanelClassName = useCardLayout ? "space-y-4" : "space-y-6 text-sm text-slate-700"

  return (
        <div data-testid="transfer-process-contacts-tab-panel" className={panelClassName}>
          <div
            data-testid="transfer-process-contacts-subtab-list"
            className="inline-flex w-full min-w-0 flex-col gap-1 rounded-2xl border border-slate-200/90 bg-slate-100/80 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] sm:w-auto sm:flex-row"
            role="tablist"
            aria-label="Employee transfer steps"
          >
            <button
              type="button"
              role="tab"
              data-testid="transfer-process-contacts-subtab-transfer-button"
              aria-selected={selectedSubTab === "transferstaff"}
              className={cn(
                "inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold tracking-tight transition-all duration-200 sm:min-h-0 sm:min-w-[148px]",
                selectedSubTab === "transferstaff"
                  ? "bg-white text-[#0f172a] shadow-[0_4px_14px_-4px_rgba(21,151,255,0.45)] ring-2 ring-[rgb(21_151_255)]/35"
                  : "text-slate-600 hover:bg-white/60 hover:text-[#1f3556]",
              )}
              onClick={() => setSelectedSubTab("transferstaff")}
            >
              {commitDone ? (
                <Check className="h-4 w-4 shrink-0 text-[#78bf0a]" strokeWidth={2.5} aria-hidden />
              ) : (
                <X className="h-4 w-4 shrink-0 text-rose-500" strokeWidth={2.5} aria-hidden />
              )}
              Transfer
            </button>
            <button
              type="button"
              role="tab"
              data-testid="transfer-process-contacts-subtab-add-verify-button"
              aria-selected={selectedSubTab === "addstaff"}
              disabled={!commitDone}
              className={cn(
                "inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold tracking-tight transition-all duration-200 sm:min-h-0 sm:min-w-[148px]",
                selectedSubTab === "addstaff"
                  ? "bg-white text-[#0f172a] shadow-[0_4px_14px_-4px_rgba(21,151,255,0.45)] ring-2 ring-[rgb(21_151_255)]/35"
                  : "text-slate-600 hover:bg-white/60 hover:text-[#1f3556]",
                !commitDone && "cursor-not-allowed opacity-45 hover:bg-transparent",
              )}
              onClick={() => setSelectedSubTab("addstaff")}
            >
              {verifyDone ? (
                <Check className="h-4 w-4 shrink-0 text-[#78bf0a]" strokeWidth={2.5} aria-hidden />
              ) : (
                <X className="h-4 w-4 shrink-0 text-rose-500" strokeWidth={2.5} aria-hidden />
              )}
              Add & Verify
            </button>
          </div>

          {selectedSubTab === "transferstaff" && (
            <div
              data-testid="transfer-process-contacts-transfer-staff-panel"
              className={subPanelClassName}
            >
              <TransferEmployeeSection
                useCardLayout={useCardLayout}
                icon={Info}
                title="Transfer Instructions"
                testId="transfer-process-contacts-transfer-instructions"
              >
              {/** Angular `transfer-process.component.html` `#transferstaff` → `.transfer-info` (three numbered `<p>`). */}
              <div
                data-testid={useCardLayout ? undefined : "transfer-process-contacts-transfer-instructions"}
                className={
                  useCardLayout
                    ? "space-y-3 text-[13px] leading-relaxed text-slate-700"
                    : "rounded-2xl border border-sky-100/80 bg-gradient-to-br from-slate-50 via-white to-[#f0f7ff] p-4 shadow-sm sm:p-5"
                }
              >
                <div className={cn("transfer-info space-y-3 text-[13px] leading-relaxed text-slate-700", !useCardLayout && "m-0")}>
                  <p data-testid="transfer-process-contacts-instruction-step-1" className="m-0">
                    1. Choose the employees you would like to mark for transfer via the checkboxes in the left column.
                    Once all employees you wish to transfer are selected, click the right arrow. This will mark them for
                    transfer.
                  </p>
                  <p data-testid="transfer-process-contacts-instruction-step-2" className="m-0">
                    2. If you wish to remove an employee previously marked for transfer, click the checkbox next to
                    their name in the right column and click the left arrow. This will mark them for removal.
                  </p>
                  <p data-testid="transfer-process-contacts-instruction-step-3" className="m-0">
                    {isSplitProperty
                      ? `3. Employees in the left column will remain on the original property and employees in the right column will move to the cloned property. This process will be saved once the 'Complete Transfer' button is clicked.`
                      : `3. Employees in the left column will be removed and employees in the right column will be transferred. This process will be saved once the 'Complete Transfer' button is clicked.`}
                  </p>
                </div>
              </div>
              </TransferEmployeeSection>
              {!(removestaffList.length || transferstaffList.length || staffList.length) ? (
                <TransferEmployeeSection
                  useCardLayout={useCardLayout}
                  icon={isSplitProperty ? Copy : Users}
                  title={isSplitProperty ? "Split Employee Transfer" : "Employee Transfer"}
                  testId="transfer-process-contacts-employee-transfer-section"
                >
                <div
                  data-testid="transfer-process-contacts-empty-state"
                  className={
                    useCardLayout
                      ? "flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-6 py-14 text-center"
                      : "flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-14 text-center"
                  }
                >
                  <p
                    data-testid="transfer-process-contacts-empty-state-title"
                    className="text-base font-semibold text-slate-700"
                  >
                    No employees in this transfer step
                  </p>
                  <p
                    data-testid="transfer-process-contacts-empty-state-description"
                    className="mt-2 max-w-md text-sm text-slate-500"
                  >
                    When {isTransferCompany ? "company" : "property"} staff load here, you can move people between remove and transfer using the arrows.
                  </p>
                  <p
                    data-testid="transfer-process-contacts-empty-state-next-hint"
                    className="mt-4 max-w-md text-sm font-medium text-slate-600"
                  >
                    Click Next below to commit this step and continue to Add & Verify.
                  </p>
                </div>
                </TransferEmployeeSection>
              ) : (
                <TransferEmployeeSection
                  useCardLayout={useCardLayout}
                  icon={isSplitProperty ? Copy : Users}
                  title={isSplitProperty ? "Split Employee Transfer" : "Employee Transfer"}
                  testId="transfer-process-contacts-employee-transfer-section"
                >
                <>
                  <div
                    data-testid="transfer-process-contacts-employee-columns"
                    className="flex min-w-0 flex-col gap-4 xl:flex-row xl:items-stretch xl:gap-3"
                  >
                    <div
                      data-testid="transfer-process-contacts-remove-table-section"
                      className={
                        useCardLayout
                          ? "flex min-h-0 min-w-0 flex-1 flex-col"
                          : "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_4px_24px_-8px_rgba(15,23,42,0.1)] ring-1 ring-slate-100"
                      }
                    >
                      {useCardLayout ? (
                        <h4
                          data-testid="transfer-process-contacts-remove-table-title"
                          className="mb-3 text-sm font-semibold text-slate-900"
                        >
                          Employees To Remove
                        </h4>
                      ) : (
                      <div className="border-b border-slate-100 bg-gradient-to-r from-rose-50/90 via-white to-white px-4 py-3 sm:px-5">
                        <h4
                          data-testid="transfer-process-contacts-remove-table-title"
                          className="text-[15px] font-semibold tracking-tight text-slate-900"
                        >
                          Employees To Remove
                        </h4>
                        {!transferContactsHelpersHidden(fragment) && (
                          <p className="mt-1 text-xs text-slate-500">
                            These contacts leave this property when you commit.
                          </p>
                        )}
                      </div>
                      )}
                      <div
                        className={
                          useCardLayout
                            ? employeeTransferTableScrollClass
                            : "h-[min(28rem,75vh)] min-w-0 overflow-x-auto overflow-y-auto"
                        }
                      >
                        <table
                          data-testid="transfer-process-contacts-remove-table"
                          className="w-full min-w-[420px] text-left text-[13px]"
                        >
                          <thead className="sticky top-0 z-[1] bg-slate-50/95 backdrop-blur-sm">
                            <tr className="border-b border-slate-200">
                              <th className="w-11 px-2 py-3 text-center sm:px-3">
                                <input
                                  type="checkbox"
                                  data-testid="transfer-process-contacts-remove-select-all-checkbox"
                                  className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                                  checked={
                                    removestaffList.length > 0 &&
                                    selectedRemoveRoleList.length === removestaffList.length
                                  }
                                  onChange={(e) => selectAllTransfer(e.target.checked, "remove")}
                                  title="Select all"
                                />
                              </th>
                              <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Name
                              </th>
                              <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Role
                              </th>
                              <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Phone
                              </th>
                              <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Email
                              </th>
                              <th className="hidden px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:table-cell">
                                Last activity
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {removestaffList.length === 0 ? (
                              <tr data-testid="transfer-process-contacts-remove-table-empty-row">
                                <td
                                  data-testid="transfer-process-contacts-remove-table-empty-message"
                                  colSpan={6}
                                  className="px-6 py-10 text-center text-sm text-slate-400"
                                >
                                  No one in the remove list. Use the left arrow from the transfer column if you need to
                                  move someone back.
                                </td>
                              </tr>
                            ) : (
                              removestaffList.map((s, idx) => (
                                <tr
                                  key={s.role_id}
                                  data-testid={`transfer-process-contacts-remove-row-${s.role_id}`}
                                  className={cn(
                                    "transition-colors hover:bg-sky-50/40",
                                    idx % 2 === 1 && "bg-slate-50/35",
                                  )}
                                >
                                  <td className="px-2 py-2.5 text-center sm:px-3">
                                    <input
                                      type="checkbox"
                                      data-testid={`transfer-process-contacts-remove-row-checkbox-${s.role_id}`}
                                      className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                                      checked={selectedRemoveRoleList.some((x) => x.role_id === s.role_id)}
                                      onChange={(e) => userSelectedTransfer(e.target.checked, s, "remove")}
                                    />
                                  </td>
                                  <td
                                    className="px-3 py-2.5 font-medium text-slate-800"
                                    data-testid={`transfer-process-contacts-remove-name-${s.role_id}`}
                                  >
                                    {s.name ?? "—"}
                                  </td>
                                  <td
                                    className="px-3 py-2.5 text-slate-700"
                                    data-testid={`transfer-process-contacts-remove-role-${s.role_id}`}
                                  >
                                    {s.role_name ? getRoleDisplayName(s.role_name) : "—"}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">
                                    {formatPhoneDisplay(s.mobile_no) || "—"}
                                  </td>
                                  <td className="max-w-[200px] break-all px-3 py-2.5 text-slate-600">{s.email ?? "—"}</td>
                                  <td className="hidden whitespace-nowrap px-3 py-2.5 text-slate-500 lg:table-cell">
                                    {s.last_active ? formatDate(s.last_active, "dd/MM/yy") : "—"}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div
                      data-testid="transfer-process-contacts-move-buttons"
                      className={cn(
                        "flex shrink-0 flex-row items-center justify-center gap-3 py-1 sm:gap-4 xl:w-[100px] xl:flex-col xl:justify-center xl:py-0",
                        useCardLayout
                          ? "xl:mt-8 xl:h-[min(28rem,75vh)]"
                          : "xl:self-stretch",
                      )}
                      aria-label="Move between lists"
                    >
                      <button
                        type="button"
                        data-testid="transfer-process-contacts-move-to-transfer-button"
                        title="Move selected to transfer list"
                        onClick={() => draftTransferStaff("transfer")}
                        className="group flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border-2 border-[rgb(21_151_255)]/25 bg-white text-[rgb(21_151_255)] shadow-md transition-all hover:scale-105 hover:border-[rgb(21_151_255)] hover:bg-[#0d7acc] hover:text-white hover:shadow-lg active:scale-95 sm:h-14 sm:w-14"
                      >
                        <ArrowRight className="h-6 w-6 transition-transform group-hover:translate-x-0.5" strokeWidth={2.25} />
                      </button>
                      <button
                        type="button"
                        data-testid="transfer-process-contacts-move-to-remove-button"
                        title="Move selected back to remove list"
                        onClick={() => draftTransferStaff("remove")}
                        className="group flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border-2 border-slate-200 bg-white text-slate-500 shadow-md transition-all hover:scale-105 hover:border-slate-400 hover:bg-slate-800 hover:text-white hover:shadow-lg active:scale-95 sm:h-14 sm:w-14"
                      >
                        <ArrowLeft className="h-6 w-6 transition-transform group-hover:-translate-x-0.5" strokeWidth={2.25} />
                      </button>
                    </div>

                    <div
                      data-testid="transfer-process-contacts-transfer-table-section"
                      className={
                        useCardLayout
                          ? "flex min-h-0 min-w-0 flex-1 flex-col"
                          : "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_4px_24px_-8px_rgba(15,23,42,0.1)] ring-1 ring-slate-100"
                      }
                    >
                      {useCardLayout ? (
                        <h4
                          data-testid="transfer-process-contacts-transfer-table-title"
                          className="mb-3 text-sm font-semibold text-slate-900"
                        >
                          Employees To Transfer
                        </h4>
                      ) : (
                      <div className="border-b border-slate-100 bg-gradient-to-r from-sky-50/90 via-white to-white px-4 py-3 sm:px-5">
                        <h4
                          data-testid="transfer-process-contacts-transfer-table-title"
                          className="text-[15px] font-semibold tracking-tight text-slate-900"
                        >
                          Employees To Transfer
                        </h4>
                        {!transferContactsHelpersHidden(fragment) && (
                          <p className="mt-1 text-xs text-slate-500">
                            These people move with the property when you commit.
                          </p>
                        )}
                      </div>
                      )}
                      <div
                        className={
                          useCardLayout
                            ? employeeTransferTableScrollClass
                            : "h-[min(28rem,75vh)] min-w-0 overflow-x-auto overflow-y-auto"
                        }
                      >
                        <table
                          data-testid="transfer-process-contacts-transfer-table"
                          className="w-full min-w-[400px] text-left text-[13px]"
                        >
                          <thead className="sticky top-0 z-[1] bg-slate-50/95 backdrop-blur-sm">
                            <tr className="border-b border-slate-200">
                              <th className="w-11 px-2 py-3 text-center sm:px-3">
                                <input
                                  type="checkbox"
                                  data-testid="transfer-process-contacts-transfer-select-all-checkbox"
                                  className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                                  checked={
                                    transferstaffList.length > 0 &&
                                    selectedTransferRoleList.length === transferstaffList.length
                                  }
                                  onChange={(e) => selectAllTransfer(e.target.checked, "transfer")}
                                  title="Select all"
                                />
                              </th>
                              <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Name
                              </th>
                              <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Role
                              </th>
                              <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Phone
                              </th>
                              <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Email
                              </th>
                              <th className="hidden px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:table-cell">
                                Last activity
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {transferstaffList.length === 0 ? (
                              <tr data-testid="transfer-process-contacts-transfer-table-empty-row">
                                <td
                                  data-testid="transfer-process-contacts-transfer-table-empty-message"
                                  colSpan={6}
                                  className="px-6 py-10 text-center text-sm text-slate-400"
                                >
                                  No one marked for transfer yet. Select rows on the left and press the blue arrow.
                                </td>
                              </tr>
                            ) : (
                              transferstaffList.map((s, idx) => (
                                <tr
                                  key={s.role_id}
                                  data-testid={`transfer-process-contacts-transfer-row-${s.role_id}`}
                                  className={cn(
                                    "transition-colors hover:bg-sky-50/40",
                                    idx % 2 === 1 && "bg-slate-50/35",
                                  )}
                                >
                                  <td className="px-2 py-2.5 text-center sm:px-3">
                                    <input
                                      type="checkbox"
                                      data-testid={`transfer-process-contacts-transfer-row-checkbox-${s.role_id}`}
                                      className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                                      checked={selectedTransferRoleList.some((x) => x.role_id === s.role_id)}
                                      onChange={(e) => userSelectedTransfer(e.target.checked, s, "transfer")}
                                    />
                                  </td>
                                  <td
                                    className="px-3 py-2.5 font-medium text-slate-800"
                                    data-testid={`transfer-process-contacts-transfer-name-${s.role_id}`}
                                  >
                                    {s.name ?? "—"}
                                  </td>
                                  <td
                                    className="px-3 py-2.5 text-slate-700"
                                    data-testid={`transfer-process-contacts-transfer-role-${s.role_id}`}
                                  >
                                    {s.role_name ? getRoleDisplayName(s.role_name) : "—"}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">
                                    {formatPhoneDisplay(s.mobile_no) || "—"}
                                  </td>
                                  <td className="max-w-[200px] break-all px-3 py-2.5 text-slate-600">{s.email ?? "—"}</td>
                                  <td className="hidden whitespace-nowrap px-3 py-2.5 text-slate-500 lg:table-cell">
                                    {s.last_active ? formatDate(s.last_active, "dd/MM/yy") : "—"}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div
                    data-testid="transfer-process-contacts-complete-transfer-footer"
                    className={cn(
                      "flex flex-col gap-3",
                      useCardLayout
                        ? "mt-5 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-end"
                        : cn(
                            "rounded-2xl border border-slate-100 bg-slate-50/40 px-4 py-4 sm:flex-row sm:items-center sm:px-5",
                            transferContactsHelpersHidden(fragment) ? "sm:justify-end" : "sm:justify-between",
                          ),
                    )}
                  >
                    {!transferContactsHelpersHidden(fragment) && (
                      <p className="text-xs leading-relaxed text-slate-500 sm:max-w-md">
                        Review both columns, then commit. You can still adjust selections until you complete this step.
                      </p>
                    )}
                    {fragment === "split_property" ? (
                      <button
                        type="button"
                        data-testid="transfer-process-contacts-complete-transfer-split-button"
                        disabled={!formState.isValid || processing || commitDone}
                        onClick={() => void completeSplitPropertyTransfer()}
                        className={cn(
                          companyFormStyles.btnPrimary,
                          "inline-flex min-h-[44px] w-full shrink-0 items-center justify-center rounded-xl px-6 py-2.5 text-sm font-semibold shadow-md transition-shadow hover:shadow-lg sm:min-h-0 sm:w-auto",
                        )}
                      >
                        Complete Transfer
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid="transfer-process-contacts-complete-transfer-button"
                        disabled={!formState.isValid || processing || commitDone}
                        onClick={() => void completeStaffTransfer()}
                        className={cn(
                          companyFormStyles.btnPrimary,
                          "inline-flex min-h-[44px] w-full shrink-0 items-center justify-center rounded-xl px-6 py-2.5 text-sm font-semibold shadow-md transition-shadow hover:shadow-lg sm:min-h-0 sm:w-auto",
                        )}
                      >
                        Complete Transfer
                      </button>
                    )}
                  </div>
                </>
                </TransferEmployeeSection>
              )}
              {!["transfer_company", "split_property"].includes(fragment) && listedmanagementList.length > 0 && (
                <TransferEmployeeSection
                  useCardLayout={useCardLayout}
                  icon={ClipboardList}
                  title="Listed Management"
                  testId="transfer-process-listed-management-section"
                  titleTestId="transfer-process-listed-management-title"
                >
                  {!useCardLayout && (
                    <p className="mb-3 text-xs text-slate-500">
                      Cross-check against Liberty records before you finish.
                    </p>
                  )}
                  <div className={useCardLayout ? employeeTableWrapClass : "min-w-0 overflow-x-auto"}>
                    <table
                      data-testid="transfer-process-listed-management-table"
                      className="min-w-[520px] w-full text-left text-[13px] sm:min-w-0"
                    >
                      <thead className="bg-slate-50/95">
                        <tr className="border-b border-slate-200">
                          <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            First name
                          </th>
                          <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Last name
                          </th>
                          <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Role
                          </th>
                          <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Matches Liberty
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {listedmanagementList.map((row, i) => (
                          <tr
                            key={i}
                            data-testid={`transfer-process-listed-management-row-${i}`}
                            className={cn("hover:bg-slate-50/60", i % 2 === 1 && "bg-slate-50/25")}
                          >
                            <td className="px-3 py-2.5 text-slate-800">{String(row.first_name ?? "")}</td>
                            <td className="px-3 py-2.5 text-slate-800">{String(row.last_name ?? "")}</td>
                            <td className="px-3 py-2.5 text-slate-700">{String(row.role ?? "")}</td>
                            <td className="px-3 py-2.5 text-slate-600">{String(row.liberty_match ?? "")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TransferEmployeeSection>
              )}
            </div>
          )}

          {selectedSubTab === "addstaff" && (
            <div
              data-testid="transfer-process-contacts-add-staff-panel"
              className={useCardLayout ? tabStyles.tabContent : "space-y-6"}
            >
              <TransferEmployeeSection
                useCardLayout={useCardLayout}
                icon={UserCheck}
                title="Transferred Employees"
                testId="transfer-process-contacts-transferred-employees-section"
                titleTestId="transfer-process-contacts-transferred-employees-title"
              >
              {useCardLayout ? (
                <div className={employeeTableWrapClass}>
                  <table
                    data-testid="transfer-process-contacts-transferred-employees-table"
                    className="min-w-[480px] w-full text-left text-[13px]"
                  >
                    <thead className="bg-slate-50/95">
                      <tr className="border-b border-slate-200">
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Name
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Role
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Phone
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Email
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {transferstaffList.length === 0 ? (
                        <tr data-testid="transfer-process-contacts-transferred-employees-empty-row">
                          <td
                            data-testid="transfer-process-contacts-transferred-employees-empty-message"
                            colSpan={4}
                            className="px-6 py-10 text-center text-sm text-slate-400"
                          >
                            No transferred employees in this step yet.
                          </td>
                        </tr>
                      ) : (
                        transferstaffList.map((s, idx) => (
                          <tr
                            key={s.role_id}
                            data-testid={`transfer-process-contacts-transferred-row-${s.role_id}`}
                            className={cn("hover:bg-emerald-50/30", idx % 2 === 1 && "bg-slate-50/30")}
                          >
                            <td className="px-3 py-2.5 font-medium text-slate-800">
                              <StaffUserEditLink
                                staff={s}
                                testId={`transfer-process-contacts-transferred-name-link-${s.role_id}`}
                                onOpenEdit={setUserEditModalUserId}
                              >
                                {s.name}
                              </StaffUserEditLink>
                            </td>
                            <td
                              className="px-3 py-2.5 text-slate-700"
                              data-testid={`transfer-process-contacts-transferred-role-${s.role_id}`}
                            >
                              {s.role_name ? getRoleDisplayName(s.role_name) : "—"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">
                              {formatPhoneDisplay(s.mobile_no) || "—"}
                            </td>
                            <td className="max-w-[220px] break-all px-3 py-2.5 text-slate-600">{s.email ?? "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
              <div
                data-testid="transfer-process-contacts-transferred-employees-section"
                className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_4px_24px_-8px_rgba(15,23,42,0.08)] ring-1 ring-slate-100"
              >
                <div className="border-b border-slate-100 bg-gradient-to-r from-emerald-50/80 via-white to-white px-4 py-3 sm:px-5">
                  <h4
                    data-testid="transfer-process-contacts-transferred-employees-title"
                    className="text-[15px] font-semibold text-slate-900"
                  >
                    Transferred employees
                  </h4>
                  {!transferContactsHelpersHidden(fragment) && (
                    <p className="mt-1 text-xs text-slate-500">Staff who are moving with this transfer.</p>
                  )}
                </div>
                <div className="min-w-0 overflow-x-auto">
                  <table
                    data-testid="transfer-process-contacts-transferred-employees-table"
                    className="min-w-[480px] w-full text-left text-[13px]"
                  >
                    <thead className="bg-slate-50/95">
                      <tr className="border-b border-slate-200">
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Name
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Role
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Phone
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Email
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {transferstaffList.length === 0 ? (
                        <tr data-testid="transfer-process-contacts-transferred-employees-empty-row">
                          <td
                            data-testid="transfer-process-contacts-transferred-employees-empty-message"
                            colSpan={4}
                            className="px-6 py-10 text-center text-sm text-slate-400"
                          >
                            No transferred employees in this step yet.
                          </td>
                        </tr>
                      ) : (
                        transferstaffList.map((s, idx) => (
                          <tr
                            key={s.role_id}
                            data-testid={`transfer-process-contacts-transferred-row-${s.role_id}`}
                            className={cn("hover:bg-emerald-50/30", idx % 2 === 1 && "bg-slate-50/30")}
                          >
                            <td className="px-3 py-2.5 font-medium text-slate-800">
                              <StaffUserEditLink
                                staff={s}
                                testId={`transfer-process-contacts-transferred-name-link-${s.role_id}`}
                                onOpenEdit={setUserEditModalUserId}
                              >
                                {s.name}
                              </StaffUserEditLink>
                            </td>
                            <td
                              className="px-3 py-2.5 text-slate-700"
                              data-testid={`transfer-process-contacts-transferred-role-${s.role_id}`}
                            >
                              {s.role_name ? getRoleDisplayName(s.role_name) : "—"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">
                              {formatPhoneDisplay(s.mobile_no) || "—"}
                            </td>
                            <td className="max-w-[220px] break-all px-3 py-2.5 text-slate-600">{s.email ?? "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              )}
              </TransferEmployeeSection>

              <TransferEmployeeSection
                useCardLayout={useCardLayout}
                icon={ClipboardCheck}
                title="New Employees"
                testId="transfer-process-contacts-new-employees-section"
                titleTestId="transfer-process-contacts-new-employees-title"
                headerActions={
                  hasPolicy(["property_contact_edit"]) ? (
                    <button
                      type="button"
                      data-testid="transfer-process-contacts-add-employee-button"
                      className={cn(
                        companyFormStyles.btnPrimary,
                        "inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold shadow-md transition-shadow hover:shadow-lg sm:min-h-0",
                      )}
                      onClick={() => openAddEmployeeModal()}
                    >
                      Add Employee
                    </button>
                  ) : undefined
                }
              >
              <div
                data-testid={useCardLayout ? undefined : "transfer-process-contacts-new-employees-section"}
                className={
                  useCardLayout
                    ? undefined
                    : "overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_4px_24px_-8px_rgba(15,23,42,0.08)] ring-1 ring-slate-100"
                }
              >
                {!useCardLayout && (
                <div className="flex flex-col gap-3 border-b border-slate-100 bg-gradient-to-r from-sky-50/80 via-white to-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div>
                    <h4
                      data-testid="transfer-process-contacts-new-employees-title"
                      className="text-[15px] font-semibold text-slate-900"
                    >
                      New employees
                    </h4>
                    {!transferContactsHelpersHidden(fragment) && (
                      <p className="mt-1 text-xs text-slate-500">Select everyone added, then verify to continue.</p>
                    )}
                  </div>
                  {hasPolicy(["property_contact_edit"]) && (
                    <button
                      type="button"
                      data-testid="transfer-process-contacts-add-employee-button"
                      className={cn(
                        companyFormStyles.btnPrimary,
                        "inline-flex min-h-[44px] w-full shrink-0 items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold shadow-md transition-shadow hover:shadow-lg sm:min-h-0 sm:w-auto",
                      )}
                      onClick={() => openAddEmployeeModal()}
                    >
                      Add Employee
                    </button>
                  )}
                </div>
                )}
                <div className={useCardLayout ? employeeTableWrapClass : "min-w-0 overflow-x-auto"}>
                  <table
                    data-testid="transfer-process-contacts-new-employees-table"
                    className="min-w-[520px] w-full text-left text-[13px]"
                  >
                    <thead className="bg-slate-50/95">
                      <tr className="border-b border-slate-200">
                        <th className="w-11 px-2 py-3 text-center sm:px-3">
                          <input
                            type="checkbox"
                            data-testid="transfer-process-contacts-new-select-all-checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                            checked={
                              addedstaffList.length > 0 &&
                              (verifyDone || selectedAddRoleList.length === addedstaffList.length)
                            }
                            onChange={(e) => selectAllTransfer(e.target.checked, "new")}
                            title="Select all"
                          />
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Name
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Role
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Phone
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Email
                        </th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {addedstaffList.map((s, idx) => {
                        const rowVerified =
                          verifyDone || selectedAddRoleList.some((x) => x.role_id === s.role_id)
                        return (
                          <tr
                            key={s.role_id}
                            data-testid={`transfer-process-contacts-new-row-${s.role_id}`}
                            className={cn("hover:bg-sky-50/30", idx % 2 === 1 && "bg-slate-50/30")}
                          >
                            <td className="px-2 py-2.5 text-center sm:px-3">
                              <input
                                type="checkbox"
                                data-testid={`transfer-process-contacts-new-row-checkbox-${s.role_id}`}
                                className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                                checked={rowVerified}
                                onChange={(e) => userSelectedTransfer(e.target.checked, s, "new")}
                              />
                            </td>
                            <td className="px-3 py-2.5 font-medium text-slate-800">
                              <StaffUserEditLink
                                staff={s}
                                testId={`transfer-process-contacts-new-name-link-${s.role_id}`}
                                onOpenEdit={setUserEditModalUserId}
                              >
                                {s.name}
                              </StaffUserEditLink>
                            </td>
                            <td
                              className="px-3 py-2.5 text-slate-700"
                              data-testid={`transfer-process-contacts-new-role-${s.role_id}`}
                            >
                              {s.role_name ? getRoleDisplayName(s.role_name) : "—"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">
                              {formatPhoneDisplay(s.mobile_no) || "—"}
                            </td>
                            <td className="max-w-[220px] break-all px-3 py-2.5 text-slate-600">{s.email ?? "—"}</td>
                            <td className="px-3 py-2.5">
                              <span
                                data-testid={`transfer-process-contacts-new-row-status-${s.role_id}`}
                                className={cn(
                                  "inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                                  rowVerified
                                    ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200/80"
                                    : "bg-amber-50 text-amber-800 ring-1 ring-amber-200/80",
                                )}
                              >
                                {rowVerified ? "Verified" : "Pending"}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div
                data-testid="transfer-process-contacts-verify-footer"
                className={cn(
                  "flex flex-col gap-3",
                  useCardLayout
                    ? "mt-5 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-end"
                    : cn(
                        "rounded-2xl border border-slate-100 bg-slate-50/40 px-4 py-4 sm:flex-row sm:items-center sm:px-5",
                        transferContactsHelpersHidden(fragment) ? "sm:justify-end" : "sm:justify-between",
                      ),
                )}
              >
                {!transferContactsHelpersHidden(fragment) && (
                  <p className="text-xs leading-relaxed text-slate-500 sm:max-w-md">
                    After everyone new is checked, verify to mark this step complete and unlock the rest of the wizard.
                  </p>
                )}
                <button
                  type="button"
                  data-testid="transfer-process-contacts-verify-button"
                  disabled={selectedAddRoleList.length !== addedstaffList.length && !verifyDone}
                  onClick={() => void completeVerify()}
                  className={cn(
                    companyFormStyles.btnPrimary,
                    "inline-flex min-h-[44px] w-full shrink-0 items-center justify-center rounded-xl px-6 py-2.5 text-sm font-semibold shadow-md transition-shadow hover:shadow-lg disabled:opacity-50 sm:min-h-0 sm:w-auto",
                  )}
                >
                  Verify
                </button>
              </div>
              </TransferEmployeeSection>
            </div>
          )}
        </div>
  )
}
